import { MapPin } from 'lucide-react';

import { ErroDaLista } from '@/componentes/ErroDaLista';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';

import { useSaidasParaAObra } from './consultas';
import { aCobrar, corDasHorasSemReportar, descreverSaida, horasSemReportar, SEM_REPORTAR_COBRAR_H } from './saida';

/**
 * O APARELHO QUE SAIU PARA A OBRA E NÃO REPORTOU (0112) — a única defesa
 * contra o aparelho que não volta.
 *
 * O cronômetro do campo resolve o turno que volta: o aparelho guarda os
 * eventos, a batida limita o prejuízo da bateria, e o apontamento entra na
 * primeira janela de sinal. Nada disso cobre o tablet quebrado na cabine nem o
 * aparelho esquecido no armário — e nesse caso o buraco é completo e calado: a
 * máquina trabalhou dez horas, a hora existe no log de um aparelho que ninguém
 * abre, e a conferência não vê falta nenhuma, porque falta de apontamento é
 * indistinguível de máquina que não operou.
 *
 * Quem fecha isso é a mesa, e ela só pode cobrar o que sabe que saiu. É para
 * isso que levar o pacote virou um ato registrado.
 *
 * ## A máquina que já reportou SAI da lista
 *
 * Ela não é um fato pendente, e deixá-la ali transformaria o cartão numa lista
 * de tudo que saiu — que ninguém lê todo dia. O cartão também desaparece
 * inteiro quando não há nada a cobrar, pelo mesmo motivo da agenda de paradas:
 * um cartão dizendo "nada" todo dia é ruído no topo da tela.
 *
 * ## O corte é da tela, e está escrito nela
 *
 * A rota devolve as horas e não decide nada. O cartão cobra a partir de
 * `SEM_REPORTAR_COBRAR_H`, e escreve a regra embaixo — indicador cujo critério
 * não aparece é número que ninguém confere.
 */
export function SaidasParaAObra({ pode }: { readonly pode: (codigo: string) => boolean }) {
  const permitido = pode('operacao.ler');
  const saidas = useSaidasParaAObra(permitido);
  const cobrar = aCobrar(saidas.data ?? []);

  if (permitido && saidas.data !== undefined && cobrar.length === 0) return null;

  return (
    <section className="cartao flex flex-col gap-4 p-4" aria-label="Saiu para a obra e não reportou">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex size-[var(--indicador-bloco)] shrink-0 items-center justify-center rounded-controle bg-standby-fundo"
        >
          <MapPin className="size-[var(--indicador-icone)] text-standby" />
        </span>
        <h2 className="txt-subtitulo text-tinta">Saiu para a obra e não reportou</h2>
      </div>
      {!permitido ? (
        <EstadoVazio
          icone={MapPin}
          tom="atencao"
          titulo="Esta lista pede outro perfil"
          descricao="Ela é da Operação: peça ao administrador da empresa o acesso à Operação (gestor operacional, planejador de manutenção ou oficina)."
        />
      ) : saidas.isPending ? (
        <ul aria-hidden="true" className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex flex-col gap-2 rounded-controle bg-rebaixada p-3">
              <Barra className="h-4 w-3/4" />
              <Barra className="h-3 w-1/2" />
            </li>
          ))}
        </ul>
      ) : saidas.isError ? (
        <ErroDaLista
          oQue="As saídas para a obra"
          erro={saidas.error}
          aoTentarDeNovo={() => {
            void saidas.refetch();
          }}
        />
      ) : (
        <>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
            {cobrar.map((s) => (
              <li
                key={`${s.equipamentoId}-${s.dia}`}
                title={s.equipamentoDescricao}
                className="flex flex-col gap-1 rounded-controle bg-rebaixada p-3"
              >
                <span className="txt-botao text-tinta">
                  <span className="codigo">{s.equipamento}</span>
                </span>
                {/* O VALOR É O PRÓPRIO ALERTA (R02), então ele é colorido — e só
                    ele: o resto da linha fica na tinta normal. */}
                <span className={`numero txt-corpo ${corDasHorasSemReportar(s.horasSemReportar)}`}>
                  {horasSemReportar(s.horasSemReportar)}
                </span>
                <span className="txt-apoio text-tinta-secundaria">
                  {descreverSaida(s)}
                </span>
              </li>
            ))}
          </ul>
          <p className="txt-apoio text-tinta-secundaria">
            {`Aparece a máquina cujo turno começou no aparelho (ou que levou o pacote, na versão anterior do app) e não teve apontamento recebido em ${String(SEM_REPORTAR_COBRAR_H)} h. Ligue para a frente: o apontamento pode estar preso num aparelho sem sinal, sem bateria ou quebrado.`}
          </p>
        </>
      )}
    </section>
  );
}
